import type { RunMetricOutput } from "@/types/RunMetricOutput"

export type LegacyQuestionType = "score" | "noul" | "choice"

export interface DecisionDistributionEntry {
  label: string
  probability: number
}

export type LegacyDecisionEstimate =
  | {
      status: "ok"
      questionType: LegacyQuestionType
      value: number | string
      confidence: number | null
      distribution: DecisionDistributionEntry[]
      model: string | null
    }
  | {
      status: "unavailable"
      questionType: LegacyQuestionType | null
      reason: string
    }

const QUESTION_TYPES = new Set<string>(["score", "noul", "choice"])
const BINARY_ORDER = ["YES", "NO"]

const UNAVAILABLE_REASONS: Record<string, string> = {
  invalid_payload:
    "Decision estimates could not be read. Review the result before relying on its confidence.",
  decision_model_unavailable: "Decision estimates were unavailable.",
  invalid_decision_distribution: "The decision estimate could not be read.",
  decision_unavailable: "Decision estimates were unavailable.",
  decision_response_invalid: "The decision estimate could not be read.",
  decision_failed: "Decision estimates were unavailable.",
  question_shape_unsupported:
    "This metric's range or categories isn't supported for probability estimates, for example a scale with more than 16 values.",
  transcript_too_large:
    "The transcript was too long to estimate probabilities.",
  question_too_large:
    "The metric question was too long to estimate probabilities.",
  empty_transcript: "The conversation had no transcript to send.",
  empty_question: "The metric has no question to send.",
  jev_unavailable: "Decision estimates were unavailable.",
  jev_gateway_response_not_object: "The decision estimate could not be read.",
  jev_response_schema_invalid: "The decision estimate could not be read.",
  jev_response_contract_invalid:
    "The decision estimate didn't match the metric's options.",
  jev_calibration_failed: "Decision estimates were unavailable.",
}

/** Read the flag-gated Jev calibration stored beside a Standard LLM Judge verdict, if any. */
export function getLegacyDecisionEstimate(
  metricOutput: Pick<RunMetricOutput, "runtime_metadata">,
): LegacyDecisionEstimate | null {
  const raw: unknown = metricOutput.runtime_metadata?.jev_calibration
  if (!isRecord(raw)) {
    return null
  }
  const questionType = toQuestionType(raw.question_type)
  if (raw.status === "unavailable") {
    return {
      status: "unavailable",
      questionType,
      reason: typeof raw.reason === "string" ? raw.reason : "unknown",
    }
  }
  if (raw.status !== "ok" || questionType === null) {
    return null
  }
  const value = toValue(raw.value, questionType)
  const distribution = toDistribution(raw.distribution, questionType)
  if (value === null || distribution === null) {
    return null
  }
  return {
    status: "ok",
    questionType,
    value,
    confidence: isProbability(raw.confidence) ? raw.confidence : null,
    distribution,
    model: typeof raw.model === "string" ? raw.model : null,
  }
}

/** Plain-language explanation for a recorded unavailable reason. */
export function describeDecisionUnavailableReason(reason: string): string {
  return UNAVAILABLE_REASONS[reason] ?? "Decision estimates weren't recorded."
}

function toQuestionType(value: unknown): LegacyQuestionType | null {
  return typeof value === "string" && QUESTION_TYPES.has(value)
    ? (value as LegacyQuestionType)
    : null
}

function toValue(
  value: unknown,
  questionType: LegacyQuestionType,
): number | string | null {
  if (questionType === "choice") {
    return typeof value === "string" && value ? value : null
  }
  if (questionType === "noul") {
    return isProbability(value) ? value : null
  }
  return typeof value === "number" && Number.isFinite(value) ? value : null
}

function toDistribution(
  value: unknown,
  questionType: LegacyQuestionType,
): DecisionDistributionEntry[] | null {
  if (!isRecord(value)) {
    return null
  }
  const entries: DecisionDistributionEntry[] = []
  for (const [label, probability] of Object.entries(value)) {
    if (!isProbability(probability)) {
      return null
    }
    entries.push({ label, probability })
  }
  if (entries.length === 0) {
    return null
  }
  if (questionType === "score") {
    return entries.sort((a, b) => Number(a.label) - Number(b.label))
  }
  if (questionType === "noul") {
    return entries.sort(
      (a, b) => BINARY_ORDER.indexOf(a.label) - BINARY_ORDER.indexOf(b.label),
    )
  }
  return entries
}

function isProbability(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 1
  )
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}
